/**
 * @fileoverview RecentItems Component - Displays recently accessed items and navigation history
 *
 * A comprehensive component that tracks and displays user navigation patterns, providing
 * quick access to recently viewed items and navigation history. Features include:
 * - Tab-based interface for Recent Items vs Navigation History
 * - Entity-specific icons and formatting
 * - Timestamp formatting with relative time display
 * - Item removal functionality with instant feedback
 * - Collapsible interface for space optimization
 * - Empty state handling with helpful messaging
 * - Responsive design for sidebar and dropdown contexts
 *
 * The component integrates with NavigationContext to:
 * - Track recently accessed entities (clients, invoices, time entries, etc.)
 * - Maintain navigation history across page visits
 * - Provide removal functionality for both recent items and history
 * - Persist user navigation patterns
 *
 * Two main components exported:
 * 1. RecentItems - Main component for sidebar integration
 * 2. RecentItemsDropdown - Dropdown variant for toolbar/header use
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigation } from '../context/NavigationContext';
import {
  FiClock,
  FiUsers,
  FiFileText,
  FiDollarSign,
  FiX,
  FiChevronDown,
  FiChevronRight,
  FiTrash2, // eslint-disable-line no-unused-vars
  FiExternalLink
} from 'react-icons/fi';
import '../styles/RecentItems.css';

/**
 * RecentItems - Displays recently accessed items and navigation history with tabbed interface
 *
 * A comprehensive navigation aid that shows recently accessed entities and page history
 * in a user-friendly tabbed interface. Integrates with the NavigationContext to track
 * user interactions and provide quick access to previously viewed content.
 *
 * Features:
 * - Tabbed interface switching between Recent Items and Navigation History
 * - Entity-aware icon display based on item type (client, invoice, user, etc.)
 * - Intelligent timestamp formatting with relative time (e.g., "2m ago", "3h ago")
 * - Individual item removal with instant UI feedback
 * - Collapsible interface for space optimization
 * - Empty state handling with helpful user guidance
 * - Responsive design that adapts to container context
 *
 * Entity Types Supported:
 * - client: Client records with user icon
 * - invoice: Invoice documents with file icon
 * - time-entry: Time tracking entries with clock icon
 * - user: User profiles with user icon
 * - payment: Payment records with dollar sign icon
 *
 * Navigation Integration:
 * - Reads from NavigationContext.recentItems for entity-specific recent access
 * - Reads from NavigationContext.navigationHistory for general page history
 * - Provides removal callbacks for both recent items and history management
 * - Maintains separate tracking for entities vs general navigation
 *
 * @param {Object} props - Component props
 * @param {number} [props.limit=10] - Maximum number of items to display in each tab
 * @param {boolean} [props.showHeader=true] - Whether to show the tabbed header interface
 * @param {boolean} [props.collapsible=true] - Whether the component can be collapsed/expanded
 * @returns {JSX.Element|null} The recent items component or null if no items exist
 */
const RecentItems = ({ limit = 10, showHeader = true, collapsible = true }) => {
  const { recentItems, navigationHistory, removeFromRecentItems, removeFromHistory } = useNavigation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('recent'); // 'recent' or 'history'

  // Get icon for entity type
  const getEntityIcon = (entityType) => {
    const icons = {
      'client': FiUsers,
      'invoice': FiFileText,
      'time-entry': FiClock,
      'user': FiUsers,
      'payment': FiDollarSign
    };
    
    return icons[entityType] || FiFileText;
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    
    return date.toLocaleDateString();
  };

  // Get display name for path
  const getDisplayName = (item) => {
    if (item.entityType && item.entityId) {
      return `${item.entityType.charAt(0).toUpperCase() + item.entityType.slice(1)} #${item.entityId}`;
    }
    return item.title || 'Untitled';
  };

  const limitedRecentItems = recentItems.slice(0, limit);
  const limitedHistory = navigationHistory.slice(0, limit);

  if (limitedRecentItems.length === 0 && limitedHistory.length === 0) {
    return null;
  }

  return (
    <div className="recent-items-container">
      {showHeader && (
        <div className="recent-items-header">
          <div className="recent-items-tabs">
            <button
              className={`recent-items-tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              Recent Items ({limitedRecentItems.length})
            </button>
            <button
              className={`recent-items-tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History ({limitedHistory.length})
            </button>
          </div>
          
          {collapsible && (
            <button
              className="recent-items-collapse"
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-label={isCollapsed ? 'Expand recent items' : 'Collapse recent items'}
            >
              {isCollapsed ? <FiChevronRight /> : <FiChevronDown />}
            </button>
          )}
        </div>
      )}

      {!isCollapsed && (
        <div className="recent-items-content">
          {activeTab === 'recent' && (
            <div className="recent-items-list">
              {limitedRecentItems.length === 0 ? (
                <div className="recent-items-empty">
                  <FiClock className="empty-icon" />
                  <p>No recent items</p>
                  <span>Visit some detail pages to see them here</span>
                </div>
              ) : (
                limitedRecentItems.map((item) => {
                  const IconComponent = getEntityIcon(item.entityType);
                  return (
                    <div key={`${item.entityType}-${item.entityId}`} className="recent-item">
                      <Link to={item.path} className="recent-item-link">
                        <div className="recent-item-icon">
                          <IconComponent />
                        </div>
                        <div className="recent-item-content">
                          <div className="recent-item-title">
                            {getDisplayName(item)}
                          </div>
                          <div className="recent-item-meta">
                            <span className="recent-item-type">
                              {item.entityType.replace('-', ' ')}
                            </span>
                            <span className="recent-item-time">
                              {formatTimestamp(item.timestamp)}
                            </span>
                          </div>
                        </div>
                        <FiExternalLink className="recent-item-external" />
                      </Link>
                      <button
                        className="recent-item-remove"
                        onClick={(e) => {
                          e.preventDefault();
                          removeFromRecentItems(item.entityType, item.entityId);
                        }}
                        aria-label="Remove from recent items"
                      >
                        <FiX />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="recent-items-list">
              {limitedHistory.length === 0 ? (
                <div className="recent-items-empty">
                  <FiClock className="empty-icon" />
                  <p>No navigation history</p>
                  <span>Navigate around to build your history</span>
                </div>
              ) : (
                limitedHistory.map((item) => (
                  <div key={item.id} className="recent-item">
                    <Link to={item.path} className="recent-item-link">
                      <div className="recent-item-icon">
                        <FiClock />
                      </div>
                      <div className="recent-item-content">
                        <div className="recent-item-title">
                          {item.title}
                        </div>
                        <div className="recent-item-meta">
                          <span className="recent-item-type">
                            {item.type}
                          </span>
                          <span className="recent-item-time">
                            {formatTimestamp(item.timestamp)}
                          </span>
                        </div>
                      </div>
                      <FiExternalLink className="recent-item-external" />
                    </Link>
                    <button
                      className="recent-item-remove"
                      onClick={(e) => {
                        e.preventDefault();
                        removeFromHistory(item.id);
                      }}
                      aria-label="Remove from history"
                    >
                      <FiX />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * RecentItemsDropdown - Dropdown variant of RecentItems for toolbar/header integration
 *
 * A compact dropdown implementation of the recent items functionality, designed for
 * integration into toolbars, headers, or other constrained UI spaces. Provides
 * the same recent items access in a space-efficient dropdown format.
 *
 * Features:
 * - Compact button trigger with item count
 * - Dropdown overlay with click-outside-to-close behavior
 * - Embedded RecentItems component without header/tabs
 * - Automatic hiding when no recent items exist
 * - Accessible keyboard and screen reader support
 * - Overlay backdrop for mobile-friendly interaction
 *
 * Use Cases:
 * - Toolbar integration for quick access
 * - Header navigation enhancement
 * - Context menus and action bars
 * - Mobile-optimized navigation
 *
 * @param {Object} props - Component props
 * @param {string} [props.buttonText="Recent"] - Text to display on the dropdown trigger button
 * @param {number} [props.limit=5] - Maximum number of items to display in the dropdown
 * @returns {JSX.Element|null} The dropdown component or null if no recent items exist
 */
export const RecentItemsDropdown = ({ buttonText = "Recent", limit = 5 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { recentItems } = useNavigation();

  const limitedItems = recentItems.slice(0, limit);

  if (limitedItems.length === 0) {
    return null;
  }

  return (
    <div className="recent-items-dropdown">
      <button
        className="recent-items-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <FiClock className="dropdown-icon" />
        {buttonText}
        <FiChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="recent-items-dropdown-content">
          <div className="recent-items-dropdown-header">
            <span>Recent Items</span>
            <button
              className="recent-items-dropdown-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close recent items"
            >
              <FiX />
            </button>
          </div>
          
          <RecentItems 
            limit={limit} 
            showHeader={false} 
            collapsible={false} 
          />
        </div>
      )}
      
      {isOpen && (
        <div 
          className="recent-items-dropdown-overlay" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default RecentItems;